/* Hallmark · component: login-dialog · genre: modern-minimal · theme: project tokens (orange --primary)
 * modes: login · register (register is OTP-gated — see useRegistrationOtp)
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass (46–50) — focus ring uses --primary-ink (0.55 0.19 40.5), ~4.5:1 on white
 *
 * The register panel shares IdentitySwitch + OtpSendButton + useRegistrationOtp with
 * pages/user/auth/register.tsx, so the cooldown handling and the axios status trap
 * (lib/axios.ts resolves 4xx) are handled in one place rather than two.
 * Login stays password-only; login OTP is a separate piece of work.
 */
import { router } from '@inertiajs/react';
import { ChevronDown, LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import ForgotPasswordInertiaController from '@/actions/App/Http/Controllers/User/Auth/ForgotPasswordInertiaController';
import LoginInertiaController from '@/actions/App/Http/Controllers/User/Auth/LoginInertiaController';
import RegisterController from '@/actions/App/Http/Controllers/User/Auth/RegisterController';
import { FormInput } from '@/components/form-input/form-input';
import { FormOtpInput } from '@/components/form-input/form-otp-input';
import { IdentitySwitch, type IdentityChannel } from '@/components/form-input/identity-switch';
import { OtpSendButton } from '@/components/form-input/otp-send-button';
import SocialLoginButtons from '@/components/social-login-buttons';
import TextLink from '@/components/text-link';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useRegistrationOtp, type OtpResponse } from '@/hooks/use-registration-otp';
import { post, toErrorMessage } from '@/lib/axios';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Typo } from '@/components/ui/typography';

// ─── Login schema ────────────────────────────────────────────────────────────
// 'identifier' is the canonical field — LoginInertiaController::findIdentifier()
// accepts either a mobile number or an email and merges the right DB column.
const loginSchema = z.object({
  identifier: z.string().min(1, { message: 'Số điện thoại hoặc email là bắt buộc' }),
  password: z.string().min(1, { message: 'Mật khẩu là bắt buộc' }),
});
type LoginForm = z.infer<typeof loginSchema>;

// ─── Register schema ──────────────────────────────────────────────────────────
// Mirrors register.tsx. Both mobile and email are individually optional;
// at least one must be provided. Consent is implicit — submitting the form
// counts as agreeing to the terms (stated in the text under the button).
const registerSchema = z
  .object({
    mobile: z.string().optional(),
    email: z.string().optional(),
    // Enforced in superRefine alongside the other cross-field rules. The field is
    // unmounted until a code has been sent, so it never blocks an earlier step.
    otp: z.string().optional(),
    password: z.string().min(6, { message: 'Mật khẩu phải có ít nhất 6 ký tự' }),
    password_confirmation: z.string().min(6, { message: 'Mật khẩu phải có ít nhất 6 ký tự' }),
  })
  .superRefine((d, ctx) => {
    const mobile = d.mobile?.trim() ?? '';
    const email  = d.email?.trim()  ?? '';

    // Only one identity field is visible at a time, so each path carries the
    // message that reads naturally on its own.
    if (!mobile && !email) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mobile'], message: 'Vui lòng nhập số điện thoại' });
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email'], message: 'Vui lòng nhập email' });
      return;
    }

    if (mobile && mobile.replace(/\D/g, '').length < 9) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mobile'], message: 'Số điện thoại không hợp lệ' });
    }
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email'], message: 'Email không hợp lệ' });
    }

    // RegisterController::validator() requires otp — the account cannot be created
    // without proving ownership of the identity above.
    if ((d.otp?.trim() ?? '').length !== 6) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['otp'], message: 'Mã OTP gồm 6 chữ số' });
    }
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Mật khẩu xác nhận không khớp',
    path: ['password_confirmation'],
  });
type RegisterForm = z.infer<typeof registerSchema>;

// Single source of truth lives with the switch component.
type IdentityType = IdentityChannel;

// ─── Component ────────────────────────────────────────────────────────────────

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful login or registration, once shared props have been reloaded. */
  onSuccess?: () => void;
  /** Relative path the user returns to after a social (OAuth) login. */
  redirectTo?: string;
}

export function LoginDialog({ open, onOpenChange, onSuccess, redirectTo }: LoginDialogProps) {
  const [processing, setProcessing] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [registerIdentity, setRegisterIdentity] = useState<IdentityType>('mobile');

  // Login form — always mounted so hook order is stable
  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: '', password: '' },
  });

  // Register form — always mounted so hook order is stable
  const registerForm = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      mobile: '',
      email: '',
      otp: '',
      password: '',
      password_confirmation: '',
    },
  });

  const otp = useRegistrationOtp({ form: registerForm, channel: registerIdentity });

  // Reset both forms, collapse the panel, and return to login mode whenever
  // the dialog is closed, so a reopened dialog always starts fresh.
  useEffect(() => {
    if (!open) {
      setManualOpen(false);
      setMode('login');
      setRegisterIdentity('mobile');
      otp.reset();
      loginForm.reset();
      registerForm.reset();
    }
  // loginForm / registerForm are stable refs from useForm, and otp.reset only
  // touches setState + the form — safe to omit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ─── Register identity switch ───────────────────────────────────────────────
  // Only one of mobile/email is shown. Clear the field being hidden so a stale
  // value is never submitted, then focus the newly revealed one. Any code already
  // sent belongs to the previous identity, so it goes too — the server re-derives
  // the channel from the payload and would reject it anyway.
  const selectRegisterIdentity = (next: IdentityType) => {
    if (next === registerIdentity) return;
    registerForm.setValue(registerIdentity, '');
    registerForm.clearErrors(['mobile', 'email', 'otp']);
    otp.reset();
    setRegisterIdentity(next);
    setTimeout(() => registerForm.setFocus(next), 0);
  };

  const otpActionButton = (
    <OtpSendButton
      sent={otp.sent}
      sending={otp.sending}
      countdown={otp.countdown}
      showResend={otp.showResend}
      onClick={otp.request}
    />
  );

  // One definition for both identity variants — only name, label, type and
  // autocomplete differ.
  const renderRegisterIdentityField = (type: IdentityType) => {
    const config =
      type === 'mobile'
        ? { label: 'Số điện thoại', placeholder: '0901234567', inputType: 'text', autoComplete: 'tel' }
        : { label: 'Email', placeholder: 'địa chỉ email', inputType: 'email', autoComplete: 'email' };

    return (
      <FormInput
        form={registerForm}
        name={type}
        label={config.label}
        placeholder={config.placeholder}
        isRequired
        id={`register-dialog-${type}`}
        disabled={otp.sent}
        render={({ onChange, value, ...field }) => (
          <div className="flex gap-2">
            <Input
              type={config.inputType}
              placeholder={config.placeholder}
              autoComplete={config.autoComplete}
              value={value}
              onChange={onChange}
              disabled={otp.sent}
              className="min-w-0 flex-1"
              {...field}
            />
            {otpActionButton}
          </div>
        )}
      />
    );
  };

  // ─── Login submit ───────────────────────────────────────────────────────────
  const handleLogin = async (data: LoginForm) => {
    setProcessing(true);
    try {
      const response = await post(LoginInertiaController.login().url, data);
      if (response?.data?.success) {
        toast.success('Đăng nhập thành công');
        loginForm.reset();
        // Stay on the current page: refresh auth + cart shared props, then
        // resume the pending action before closing the dialog.
        router.reload({
          only: ['auth', 'cart', 'cartCount', 'cartDiscount'],
          onFinish: () => {
            onSuccess?.();
            onOpenChange(false);
          },
        });
      } else {
        // Failed login resolves (not throws): axios validateStatus accepts 422,
        // and the controller returns a bare { error } body with no `data` key.
        const failure = response as unknown as { error?: unknown; message?: unknown };
        toast.error(toErrorMessage(failure?.error ?? failure?.message, 'Số điện thoại hoặc mật khẩu không đúng'));
      }
    } catch (error: any) {
      toast.error(error?.message || 'Có lỗi xảy ra khi đăng nhập');
    } finally {
      setProcessing(false);
    }
  };

  // ─── Register submit ────────────────────────────────────────────────────────
  const handleRegister = async (data: RegisterForm) => {
    setProcessing(true);
    try {
      const response = (await post(RegisterController.register().url, data)) as unknown as OtpResponse;

      if (response?.status !== 'error' && response?.data?.success) {
        toast.success('Tạo tài khoản thành công');
        otp.reset();
        registerForm.reset();
        // Registration auto-logs-in (RegisterController::register calls guard()->login($user)).
        // Reload shared props to surface the new session, then resume the pending action.
        router.reload({
          only: ['auth', 'cart', 'cartCount', 'cartDiscount'],
          onFinish: () => {
            onSuccess?.();
            onOpenChange(false);
          },
        });
      } else {
        // 422 resolves rather than throws (validateStatus <500).
        // Map per-field errors back to the form so uniqueness and OTP failures
        // land under the right input, then also toast the top-level message.
        Object.entries(response?.errors ?? {}).forEach(([field, msgs]) => {
          registerForm.setError(field as keyof RegisterForm, { message: msgs[0] });
        });
        // The stored code is gone (expired, or the attempt limit was hit), so
        // drop back to the pre-send state instead of leaving a dead form.
        if (response?.requires_resend) otp.reset();
        toast.error(toErrorMessage(response?.message, 'Đăng ký không thành công'));
      }
    } catch (error: any) {
      toast.error(error?.message || 'Có lỗi xảy ra khi đăng ký');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* z-index above the service cart drawer (z-[52]) — the dialog can open on top of it */}
      <DialogContent className="z-[60] max-h-[90vh] min-h-80! overflow-y-auto rounded-2xl sm:max-w-md" overlayClassName="z-[55]">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl">Đăng nhập hoặc Đăng ký</DialogTitle>
          <DialogDescription>
            {mode === 'login'
              ? 'Đăng nhập để tiếp tục đặt vé và quản lý đơn hàng của bạn'
              : 'Tạo tài khoản để bắt đầu đặt vé và quản lý đơn hàng'}
          </DialogDescription>
        </DialogHeader>

        {/* Social provider rows — no divider; the "Lựa chọn khác" link is the separator */}
        <SocialLoginButtons redirectTo={redirectTo} showDivider={false} />

        {/* Manual login / register disclosure */}
        <Collapsible open={manualOpen} onOpenChange={setManualOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="flex w-full items-center justify-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-transparent whitespace-nowrap"
            >
              Lựa chọn khác
              <ChevronDown
                className="size-4 transition-transform duration-200 motion-reduce:transition-none data-[state=open]:rotate-180"
                data-state={manualOpen ? 'open' : 'closed'}
              />
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="animate-in fade-in-0 slide-in-from-top-2 duration-200 motion-reduce:animate-none">
            {/* key={mode} re-triggers the entry animation on each swap */}
            <div key={mode} className="mt-4 animate-in fade-in-0 slide-in-from-top-1 duration-150 motion-reduce:animate-none">
              {mode === 'login' ? (
                /* ── Login panel ─────────────────────────────────────────── */
                <Form {...loginForm}>
                  <form className="flex flex-col gap-4" onSubmit={loginForm.handleSubmit(handleLogin)}>
                    <FormInput
                      form={loginForm}
                      name="identifier"
                      label="Số điện thoại hoặc email"
                      placeholder="0901234567 hoặc ban@email.com"
                      isRequired
                      id="login-dialog-identifier"
                    />

                    <FormInput
                      form={loginForm}
                      type="password"
                      name="password"
                      label="Mật khẩu"
                      placeholder="Mật khẩu"
                      isRequired
                      togglePassword
                      id="login-dialog-password"
                    />

                    <div className="flex items-center justify-end">
                      <TextLink
                        className="text-sm text-primary hover:text-primary/80"
                        href={ForgotPasswordInertiaController.showLinkRequestForm().url}
                      >
                        Quên mật khẩu?
                      </TextLink>
                    </div>

                    <Button type="submit" className="h-14 w-full rounded-xl" disabled={processing}>
                      {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                      Đăng nhập
                    </Button>

                    {/* Register swap link — lives inside the disclosure, not below it */}
                    <p className="text-muted-foreground text-sm">
                      Chưa có tài khoản?{' '}
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-sm font-semibold text-foreground"
                        onClick={() => {
                          setMode('register');
                          setRegisterIdentity('mobile');
                          otp.reset();
                          registerForm.reset();
                        }}
                      >
                        Tạo tài khoản
                      </Button>
                    </p>
                  </form>
                </Form>
              ) : (
                /* ── Register panel ──────────────────────────────────────── */
                <Form {...registerForm}>
                  <form className="flex flex-col gap-4" onSubmit={registerForm.handleSubmit(handleRegister)}>
                    {/* Channel choice leads the panel: pick how you register before
                        being asked to type anything. Disabled only while a send is
                        actually in flight — switching otherwise resets the code. */}
                    <IdentitySwitch
                      value={registerIdentity}
                      onChange={selectRegisterIdentity}
                      sent={otp.sent}
                      loading={otp.sending}
                      disabled={processing}
                      error={!!registerForm.formState.errors[registerIdentity]}
                    />

                    <div className="flex flex-col gap-4">
                      {renderRegisterIdentityField(registerIdentity)}

                      {otp.sent && <FormOtpInput form={registerForm} id="register-dialog-otp" />}

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormInput
                          form={registerForm}
                          type="password"
                          name="password"
                          label="Mật khẩu"
                          placeholder="Mật khẩu"
                          isRequired
                          togglePassword
                          id="register-dialog-password"
                        />
                        <FormInput
                          form={registerForm}
                          type="password"
                          name="password_confirmation"
                          label="Nhập lại mật khẩu"
                          placeholder="Nhập lại mật khẩu"
                          isRequired
                          togglePassword
                          id="register-dialog-password-confirm"
                        />
                      </div>
                    </div>

                    <Typo variant='muted' className='text-xs'>
                      Bằng việc nhấn "Tạo tài khoản", bạn đồng ý với Điều khoản dịch vụ và Chính sách bảo mật của chúng tôi.
                    </Typo>
                    <Button type="submit" className="h-12 w-full rounded-xl" disabled={processing || !otp.sent}>
                      {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                      Tạo tài khoản
                    </Button>

                    {/* Login swap link */}
                    <p className="text-muted-foreground text-sm">
                      Đã có tài khoản?{' '}
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-sm font-semibold text-foreground"
                        onClick={() => {
                          setMode('login');
                          otp.reset();
                          loginForm.reset();
                        }}
                      >
                        Đăng nhập
                      </Button>
                    </p>
                  </form>
                </Form>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </DialogContent>
    </Dialog>
  );
}
